import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Person, Email, AdminPanelSettings } from "@mui/icons-material";
import axios from "axios";
import EditCompanyUser from "./EditCompanyUser";
import InviteTeamMember from "./InviteTeamMember";

/**
 * Company Users Component - Manages team members for the company
 *
 * Allows company admins to view and manage their team members,
 * including role assignments and user details
 *
 * @param {Object} props - Component props
 * @param {number} props.companyId - The company ID to manage users for
 * @returns {JSX.Element} - The rendered company users component
 */
function CompanyUsers({ companyId }) {
  const [users, setUsers] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API endpoints
  const getCompanyUsersApi =
    process.env.REACT_APP_API_URL + "/api/get-company-users";
  const getCompanyDetailsApi =
    process.env.REACT_APP_API_URL + "/api/get-company-details";

  /**
   * Gets the JWT token from cookies for API authentication
   */
  const getAuthToken = () => {
    const cookies = document.cookie.split(";");
    let token = null;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "_auth") {
        token = decodeURIComponent(value);
        break;
      }
    }

    return token;
  };

  /**
   * Get user role label based on type ID
   */
  const getUserRoleLabel = (typeId) => {
    switch (typeId) {
      case 1:
        return "Company User";
      case 2:
        return "Company Admin";
      case 3:
        return "Employee";
      case 4:
        return "Admin";
      default:
        return "User";
    }
  };

  /**
   * Get role chip color based on user type
   */
  const getRoleChipColor = (typeId) => {
    switch (typeId) {
      case 4: // Admin
        return "error";
      case 3: // Employee
        return "info";
      case 2: // Company Admin
        return "secondary";
      case 1: // Company User
      default:
        return "default";
    }
  };

  /**
   * Fetch company details and users
   */
  const fetchCompanyData = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      // Fetch both company details and users
      const [detailsResponse, usersResponse] = await Promise.all([
        axios.get(`${getCompanyDetailsApi}?companyId=${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }),
        axios.get(`${getCompanyUsersApi}?companyId=${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }),
      ]);

      if (detailsResponse.data && detailsResponse.data.success) {
        setCompanyDetails(detailsResponse.data.data);
      }

      if (usersResponse.data && usersResponse.data.success) {
        setUsers(usersResponse.data.data);
      } else {
        setError("Failed to load team members");
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      if (error.response && error.response.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to load company data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, getCompanyDetailsApi, getCompanyUsersApi]);

  /**
   * Fetch company users only (for refresh after edits)
   */
  const fetchCompanyUsers = useCallback(async () => {
    if (!companyId) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${getCompanyUsersApi}?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data && response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company users:", error);
    }
  }, [companyId, getCompanyUsersApi]);

  // Effects
  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  /**
   * Generate avatar initials from name
   */
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /**
   * Handle user edit completion - refresh the users list
   */
  const handleUserEdited = useCallback(() => {
    fetchCompanyUsers();
  }, [fetchCompanyUsers]);

  /**
   * Handle invitation sent - refresh the users list
   */
  const handleInviteSent = useCallback(() => {
    // Note: New users won't appear until they actually sign up
    // But we could show a success message or update some counter
    console.log("Invitation sent successfully");
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {error && (
        <Alert
          severity='error'
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Grid
        container
        spacing={3}
      >
        {/* Team Overview Stats */}
        <Grid
          item
          xs={12}
          md={4}
        >
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant='h4'
              color='primary'
              fontWeight='bold'
            >
              {users.length}
            </Typography>
            <Typography
              variant='body1'
              color='text.secondary'
            >
              Total Team Members
            </Typography>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant='h4'
              color='secondary'
              fontWeight='bold'
            >
              {users.filter((user) => user.type_id === 2).length}
            </Typography>
            <Typography
              variant='body1'
              color='text.secondary'
            >
              Company Admins
            </Typography>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
        >
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography
              variant='h4'
              color='info.main'
              fontWeight='bold'
            >
              {users.filter((user) => user.type_id === 1).length}
            </Typography>
            <Typography
              variant='body1'
              color='text.secondary'
            >
              Company Users
            </Typography>
          </Paper>
        </Grid>

        {/* Team Members List */}
        <Grid
          item
          xs={12}
        >
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant='h6'
                fontWeight='bold'
              >
                Team Members
              </Typography>
              <InviteTeamMember
                companyId={companyId}
                companyName={companyDetails?.name || "Your Company"}
                onInviteSent={handleInviteSent}
              />
            </Box>

            {users.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Person sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                <Typography
                  variant='h6'
                  color='text.secondary'
                >
                  No team members found
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  Start by adding team members to your company
                </Typography>
              </Box>
            ) : (
              <List>
                {users.map((user, index) => (
                  <React.Fragment key={user.uid}>
                    <ListItem
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {getInitials(user.name)}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant='subtitle1'
                              fontWeight='bold'
                            >
                              {user.name}
                            </Typography>
                            <Chip
                              label={getUserRoleLabel(user.type_id)}
                              size='small'
                              color={getRoleChipColor(user.type_id)}
                              variant='outlined'
                            />
                            {user.type_id === 2 && (
                              <AdminPanelSettings
                                fontSize='small'
                                color='secondary'
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Email
                                fontSize='small'
                                color='action'
                              />
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                {user.contact || "No email provided"}
                              </Typography>
                            </Box>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              User ID: {user.uid}
                            </Typography>
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        <EditCompanyUser
                          user={user}
                          onUserEdited={handleUserEdited}
                          companyUsers={users}
                          companyId={companyId}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CompanyUsers;
